import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  IPractitionerProportionInvoiceAmount,
  TransactionAllocation,
} from '@principle-theorem/principle-core';
import {
  IInvoice,
  IStaffer,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  INamedDocument,
  isSameRef,
  type WithRef,
} from '@principle-theorem/shared';

export interface ITransactionAttributeToDialogResponse {
  attributedTo?: DocumentReference<IStaffer>;
}

export interface ITransactionAttributeToDialogRequest {
  transaction: WithRef<ITransaction>;
  invoice: WithRef<IInvoice>;
  invoicePractitioners: IPractitionerProportionInvoiceAmount[];
}

@Component({
  selector: 'pr-transaction-attribute-to-dialog',
  templateUrl: './transaction-attribute-to-dialog.component.html',
  styleUrls: ['./transaction-attribute-to-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TransactionAttributeToDialogComponent {
  private _snackBar = inject(MatSnackBar);
  form = new TypedFormGroup<ITransactionAttributeToDialogResponse>({
    attributedTo: new TypedFormControl<DocumentReference<IStaffer> | undefined>(
      undefined
    ),
  });
  staff: INamedDocument<IStaffer>[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ITransactionAttributeToDialogRequest,
    private _dialogRef: MatDialogRef<
      TransactionAttributeToDialogComponent,
      ITransactionAttributeToDialogResponse
    >
  ) {
    this.form.patchValue(data.transaction, { emitEvent: false });
    this.staff = data.invoicePractitioners.map(
      (proportion) => proportion.practitioner
    );
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    const attributedTo = this.form.value.attributedTo;
    if (!attributedTo) {
      this._dialogRef.close(this.form.value);
      return;
    }

    const currentAttributedTo = this.data.transaction.attributedTo;
    const amount = this.data.transaction.amount;

    const currentTransactionAmount =
      currentAttributedTo && isSameRef(currentAttributedTo, attributedTo)
        ? amount
        : 0;

    const validationError =
      await TransactionAllocation.validateTransactionAllocation(
        this.data.invoice,
        { amount, attributedTo },
        currentTransactionAmount
      );

    if (validationError) {
      this._snackBar.open(validationError, 'OK', { duration: 10000 });
      return;
    }

    this._dialogRef.close(this.form.value);
  }
}
